/**
 * Delegation panel — advice document workflow and delegation management.
 *
 * Shows the current advice status, enables delegation to another user
 * (with instructions), return to owner, and status progression through
 * the advice lifecycle: Drafting → Delegated → Reviewing → Finalised.
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  Input,
  Textarea,
  Text,
  Card,
  CardHeader,
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowUndo20Regular } from '@fluentui/react-icons/svg/arrow-undo';
import { Dismiss20Regular } from '@fluentui/react-icons/svg/dismiss';
import { Eye20Regular } from '@fluentui/react-icons/svg/eye';
import { PersonAdd20Regular } from '@fluentui/react-icons/svg/person-add';
import { Play20Regular } from '@fluentui/react-icons/svg/play';
import { useGateway } from '../../api/useGateway';
import { useAuth } from '../../auth/auth-context';
import { GatewayError } from '../../api/gateway-client';
import type { AdviceWorkflowSummary } from '../../api/gateway-client';
import type { AdviceStatus } from '@alterspective/shared';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DelegationPanelProps {
  matterId: string | null;
}

// ---------------------------------------------------------------------------
// Status display helpers
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<AdviceStatus, string> = {
  created: 'Created',
  drafting: 'Drafting',
  delegated: 'Delegated',
  reviewing: 'Reviewing',
  finalised: 'Finalised',
  sent: 'Sent',                 // Phase 1 Outlook handoff (OH-04)
  verified: 'Verified',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<AdviceStatus, 'informative' | 'success' | 'warning' | 'danger' | 'important'> = {
  created: 'informative',
  drafting: 'informative',
  delegated: 'warning',
  reviewing: 'important',
  finalised: 'important',       // OH-04: was 'success'; now mid-flight (awaiting send)
  sent: 'success',              // Phase 1 Outlook handoff completion
  verified: 'success',
  cancelled: 'danger',
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const BRAND_COLOR = '#075156';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '4px 0',
  },
  label: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  value: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  delegateForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
  },
  actionRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  history: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '150px',
    overflowY: 'auto',
  },
  historyItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '11px',
    padding: '4px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
  },
  historyNote: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '32px 16px',
    color: tokens.colorNeutralForeground3,
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DelegationPanel: React.FC<DelegationPanelProps> = ({ matterId }) => {
  const styles = useStyles();
  const gateway = useGateway();
  const { user } = useAuth();
  const [workflow, setWorkflow] = useState<AdviceWorkflowSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDelegateForm, setShowDelegateForm] = useState(false);

  // Delegate form state
  const [delegateName, setDelegateName] = useState('');
  const [delegateId, setDelegateId] = useState('');
  const [instructions, setInstructions] = useState('');
  const [returnNote, setReturnNote] = useState('');

  // Load workflow when matter changes
  const loadWorkflow = useCallback(async () => {
    if (!gateway || !matterId) return;

    setIsLoading(true);
    setError(null);

    try {
      const wf = await gateway.getAdviceWorkflow(matterId);
      setWorkflow(wf);
    } catch (err: unknown) {
      if (err instanceof GatewayError && err.statusCode === 404) {
        // No workflow yet — that's OK
        setWorkflow(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load workflow');
      }
    } finally {
      setIsLoading(false);
    }
  }, [gateway, matterId]);

  useEffect(() => {
    loadWorkflow();
  }, [loadWorkflow]);

  // Create workflow
  const handleCreate = useCallback(async () => {
    if (!gateway || !matterId) return;
    setIsActing(true);
    setError(null);
    try {
      const wf = await gateway.createAdviceWorkflow(matterId);
      setWorkflow(wf);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create workflow');
    } finally {
      setIsActing(false);
    }
  }, [gateway, matterId]);

  // Delegate
  const handleDelegate = useCallback(async () => {
    if (!gateway || !workflow || !delegateName.trim()) return;
    setIsActing(true);
    setError(null);
    try {
      const wf = await gateway.delegateAdvice(workflow.id, {
        delegateId: delegateId.trim() || delegateName.trim().toLowerCase().replace(/\s+/g, '.'),
        delegateName: delegateName.trim(),
        instructions: instructions.trim() || undefined,
      });
      setWorkflow(wf);
      setShowDelegateForm(false);
      setDelegateName('');
      setDelegateId('');
      setInstructions('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delegation failed');
    } finally {
      setIsActing(false);
    }
  }, [gateway, workflow, delegateId, delegateName, instructions]);

  // Return to owner
  const handleReturn = useCallback(async () => {
    if (!gateway || !workflow) return;
    setIsActing(true);
    setError(null);
    try {
      const wf = await gateway.returnAdvice(workflow.id, returnNote.trim() || undefined);
      setWorkflow(wf);
      setReturnNote('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Return failed');
    } finally {
      setIsActing(false);
    }
  }, [gateway, workflow, returnNote]);

  // Transition to reviewing
  const handleStartReview = useCallback(async () => {
    if (!gateway || !workflow) return;
    setIsActing(true);
    setError(null);
    try {
      const wf = await gateway.transitionAdvice(workflow.id, {
        status: 'reviewing',
        note: 'Owner started review',
      });
      setWorkflow(wf);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Transition failed');
    } finally {
      setIsActing(false);
    }
  }, [gateway, workflow]);

  // Cancel
  const handleCancel = useCallback(async () => {
    if (!gateway || !workflow) return;
    setIsActing(true);
    setError(null);
    try {
      const wf = await gateway.transitionAdvice(workflow.id, {
        status: 'cancelled',
        note: 'Advice cancelled by user',
      });
      setWorkflow(wf);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Cancellation failed');
    } finally {
      setIsActing(false);
    }
  }, [gateway, workflow]);

  // Determine available actions based on status and current user.
  const isOwner = Boolean(workflow && user && workflow.ownerId === user.userId);
  const isDelegate = Boolean(workflow && user && workflow.delegateId === user.userId);
  const canUseOwnerActions = isOwner;
  const canReturnDelegatedWork = isOwner || isDelegate;
  const showNoRoleActions = Boolean(
    workflow
    && ['drafting', 'delegated', 'reviewing'].includes(workflow.status)
    && !canUseOwnerActions
    && !canReturnDelegatedWork,
  );

  if (!gateway) {
    return (
      <div className={styles.emptyState}>
        <Text>Not connected to gateway</Text>
      </div>
    );
  }

  if (!matterId) {
    return (
      <div className={styles.emptyState}>
        <Text>Select a matter first to manage the advice workflow</Text>
      </div>
    );
  }

  if (isLoading) {
    return <Spinner label="Loading workflow..." size="small" />;
  }

  return (
    <div className={styles.container}>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* No workflow yet — offer to create one */}
      {!workflow && (
        <div className={styles.emptyState}>
          <Text>No advice workflow for this matter yet.</Text>
          <div style={{ marginTop: '12px' }}>
            <Button
              appearance="primary"
              icon={<Play20Regular />}
              disabled={isActing}
              onClick={handleCreate}
              style={{ backgroundColor: BRAND_COLOR }}
            >
              {isActing ? 'Starting...' : 'Start Advice Workflow'}
            </Button>
          </div>
        </div>
      )}

      {/* Active workflow */}
      {workflow && (
        <>
          {/* Status card */}
          <Card size="small">
            <CardHeader
              header={<Text weight="semibold">Document Status</Text>}
              description={
                <div className={styles.statusRow}>
                  <Badge
                    appearance="filled"
                    color={STATUS_COLORS[workflow.status]}
                    size="medium"
                  >
                    {STATUS_LABELS[workflow.status]}
                  </Badge>
                  <Text style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                    {new Date(workflow.lastUpdated).toLocaleString()}
                  </Text>
                </div>
              }
            />
          </Card>

          {/* Owner / delegate info */}
          <div className={styles.statusSection}>
            <div className={styles.field}>
              <Text className={styles.label}>Owner</Text>
              <Text className={styles.value}>{workflow.ownerName}</Text>
            </div>

            {workflow.delegateId && (
              <>
                <div className={styles.field}>
                  <Text className={styles.label}>Delegated To</Text>
                  <Text className={styles.value}>{workflow.delegateName}</Text>
                </div>
                {workflow.delegateInstructions && (
                  <div className={styles.field}>
                    <Text className={styles.label}>Instructions</Text>
                    <Text className={styles.value}>{workflow.delegateInstructions}</Text>
                  </div>
                )}
              </>
            )}
          </div>

          <Divider />

          {/* Actions based on current status */}
          <div>
            <Text className={styles.sectionTitle}>Actions</Text>
            <div className={styles.actionRow} style={{ marginTop: '8px' }}>
              {/* Drafting: can delegate or start review */}
              {workflow.status === 'drafting' && canUseOwnerActions && (
                <>
                  <Button
                    appearance="primary"
                    icon={<PersonAdd20Regular />}
                    size="small"
                    disabled={isActing}
                    onClick={() => setShowDelegateForm(true)}
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    Delegate
                  </Button>
                  <Button
                    appearance="outline"
                    icon={<Eye20Regular />}
                    size="small"
                    disabled={isActing}
                    onClick={handleStartReview}
                  >
                    Start Review
                  </Button>
                  <Button
                    appearance="subtle"
                    icon={<Dismiss20Regular />}
                    size="small"
                    disabled={isActing}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </>
              )}

              {/* Delegated: delegate can return */}
              {workflow.status === 'delegated' && canReturnDelegatedWork && (
                <Button
                  appearance="primary"
                  icon={<ArrowUndo20Regular />}
                  size="small"
                  disabled={isActing}
                  onClick={handleReturn}
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  {isOwner ? 'Recall to Drafting' : 'Return to Owner'}
                </Button>
              )}

              {/* Reviewing: owner can go back to drafting or proceed to finalise (via Finalise tab) */}
              {workflow.status === 'reviewing' && canUseOwnerActions && (
                <>
                  <MessageBar intent="info">
                    <MessageBarBody>
                      Ready for final review. Use the <strong>Finalise</strong> tab to submit reserves.
                    </MessageBarBody>
                  </MessageBar>
                  <Button
                    appearance="subtle"
                    size="small"
                    disabled={isActing}
                    onClick={async () => {
                      if (!gateway) return;
                      setIsActing(true);
                      try {
                        const wf = await gateway.transitionAdvice(workflow.id, {
                          status: 'drafting',
                          note: 'Returned to drafting for further edits',
                        });
                        setWorkflow(wf);
                      } catch (err: unknown) {
                        setError(err instanceof Error ? err.message : 'Transition failed');
                      } finally {
                        setIsActing(false);
                      }
                    }}
                  >
                    Back to Drafting
                  </Button>
                </>
              )}

              {showNoRoleActions && (
                <MessageBar intent="info">
                  <MessageBarBody>No actions available for your role on this workflow.</MessageBarBody>
                </MessageBar>
              )}

              {workflow.status === 'finalised' && (
                <MessageBar intent="info">
                  <MessageBarBody>
                    Reserves submitted. Delivery confirmation or verification is the next step.
                    {workflow.finalisedDate && ` Finalised: ${new Date(workflow.finalisedDate).toLocaleDateString()}`}
                  </MessageBarBody>
                </MessageBar>
              )}

              {workflow.status === 'sent' && (
                <MessageBar intent="success">
                  <MessageBarBody>
                    Advice sent. Awaiting verification.
                    {workflow.sentDate && ` Sent: ${new Date(workflow.sentDate).toLocaleDateString()}`}
                  </MessageBarBody>
                </MessageBar>
              )}

              {workflow.status === 'verified' && (
                <MessageBar intent="success">
                  <MessageBarBody>
                    Advice verified.
                    {workflow.finalisedDate && ` Finalised: ${new Date(workflow.finalisedDate).toLocaleDateString()}`}
                    {workflow.sentDate && ` Sent: ${new Date(workflow.sentDate).toLocaleDateString()}`}
                    {workflow.verifiedDate && ` Verified: ${new Date(workflow.verifiedDate).toLocaleDateString()}`}
                  </MessageBarBody>
                </MessageBar>
              )}

              {workflow.status === 'cancelled' && (
                <MessageBar intent="warning">
                  <MessageBarBody>This advice workflow has been cancelled.</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </div>

          {/* Delegate form (expandable) */}
          {showDelegateForm && canUseOwnerActions && (
            <>
              <Divider />
              <div className={styles.delegateForm}>
                <Text className={styles.sectionTitle}>Delegate Document</Text>
                <Input
                  placeholder="Delegate name"
                  value={delegateName}
                  onChange={(_e, data) => setDelegateName(data.value)}
                  size="small"
                />
                <Input
                  placeholder="Delegate ID (optional — auto-generated from name)"
                  value={delegateId}
                  onChange={(_e, data) => setDelegateId(data.value)}
                  size="small"
                />
                <Textarea
                  placeholder="Instructions (e.g., 'Draft the quantum section and fill in medical costs')"
                  value={instructions}
                  onChange={(_e, data) => setInstructions(data.value)}
                  size="small"
                  rows={3}
                />
                <div className={styles.actionRow}>
                  <Button
                    appearance="primary"
                    size="small"
                    disabled={isActing || !delegateName.trim()}
                    onClick={handleDelegate}
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    {isActing ? 'Delegating...' : 'Confirm Delegation'}
                  </Button>
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => setShowDelegateForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Return note (when delegated) */}
          {workflow.status === 'delegated' && canReturnDelegatedWork && (
            <div className={styles.delegateForm}>
              <Textarea
                placeholder="Note for the owner (optional)"
                value={returnNote}
                onChange={(_e, data) => setReturnNote(data.value)}
                size="small"
                rows={2}
              />
            </div>
          )}

          {/* History */}
          {workflow.history.length > 0 && (
            <>
              <Divider />
              <Text className={styles.sectionTitle}>History</Text>
              <div className={styles.history}>
                {workflow.history.map((h, i) => (
                  <div key={i} className={styles.historyItem}>
                    <span>
                      <Badge
                        appearance="outline"
                        color={STATUS_COLORS[h.to]}
                        size="tiny"
                      >
                        {STATUS_LABELS[h.from]} → {STATUS_LABELS[h.to]}
                      </Badge>
                      <span style={{ marginLeft: '8px' }}>{h.changedByName}</span>
                    </span>
                    <span>{new Date(h.timestamp).toLocaleString()}</span>
                    {h.note && <div className={styles.historyNote}>{h.note}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
